import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";
import { agentsHref } from "@/lib/atwAgentsHost";
import { connectionKindLabel, type ConnectionStatus } from "@/lib/agentsDemoData";

function statusBadge(status: ConnectionStatus) {
  if (status === "connected") return <Badge>Connected</Badge>;
  if (status === "error") return <Badge variant="destructive">Error</Badge>;
  if (status === "pending") return <Badge variant="secondary">Pending</Badge>;
  return <Badge variant="outline">Not connected</Badge>;
}

export function ConnectionsPage() {
  const { org, may, viewOnly, setConnectionStatus } = useAgents();
  const navigate = useNavigate();
  const canManage = may("manage_connections");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Connections</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Connect a system for your organization. Agents can use it after it is connected.
          </p>
        </div>
        <Button
          type="button"
          disabled={!canManage}
          onClick={() => navigate(agentsHref("/connections/new"))}
          title={
            viewOnly
              ? "Standard role is view only. Ask a Super Admin to add a connection."
              : "Add a connection"
          }
        >
          <Plus className="size-4" aria-hidden />
          Connection
        </Button>
      </div>

      {viewOnly ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Ask a Super Admin to add a connection.
        </p>
      ) : null}

      {org.connections.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">No systems connected yet</CardTitle>
            <CardDescription>Connecting a system comes next.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {org.connections.map((conn) => (
            <Card key={conn.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base">{conn.name}</CardTitle>
                  <CardDescription>{connectionKindLabel(conn.kind)}</CardDescription>
                </div>
                {statusBadge(conn.status)}
              </CardHeader>
              <CardContent className="space-y-3">
                {conn.note ? <p className="text-sm text-muted-foreground">{conn.note}</p> : null}
                <Button
                  type="button"
                  size="sm"
                  variant={conn.status === "connected" ? "outline" : "default"}
                  disabled={!canManage}
                  onClick={() => {
                    if (conn.status === "connected") {
                      setConnectionStatus(conn.id, "pending");
                      toast.success(`${conn.name}: reconnect queued in this browser.`);
                      return;
                    }
                    setConnectionStatus(conn.id, "pending");
                    toast.success(`${conn.name}: connect queued in this browser.`);
                    window.setTimeout(() => {
                      setConnectionStatus(conn.id, "connected");
                      toast.success(`${conn.name} marked connected in this browser.`);
                    }, 1200);
                  }}
                >
                  {conn.status === "connected" ? "Reconnect" : "Connect"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
